"use client";

import { SAKA_COOKIE_NAMES } from "@/constants/cookieNames";
import { DATA_LAYER_EVENT_NAMES } from "@/constants/dataLayer";
import { useModal } from "@/contexts/ModalContext";
import { useIsMobile } from "@/hooks/useIsMobile";
import { useTranslationDetector } from "@/hooks/useTranslationDetector";
import { authService } from "@/services/authService";
import { getClientSession } from "@/utils/sakaClientSessionHelper";
import { sendGTMEvent } from "@next/third-parties/google";
import Cookies from "js-cookie";
import {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";

const AuthContext = createContext();

export function AuthProvider({ children, initialSession = null }) {
  const [loading, setLoading] = useState(!initialSession);
  const [isLogin, setIsLogin] = useState(() => {
    if (initialSession) {
      return initialSession.IsLoggedIn === true || !!initialSession.User;
    }
    return false;
  });
  const [session, setSession] = useState(initialSession);
  const [activeTab, setActiveTab] = useState("login"); // "login", "register", "forgotPassword"
  const loginModal = useModal("login-modal");
  const isMobile = useIsMobile();
  const t = useTranslationDetector();

  useLayoutEffect(() => {
    if (initialSession) return;
    const sessionData = getClientSession();
    if (sessionData) {
      setSession(sessionData);
      if (sessionData.IsLoggedIn === true) {
        setIsLogin(true);
      } else if (sessionData.IsLoggedIn === false) {
        setIsLogin(false);
      } else if (sessionData.User) {
        setIsLogin(true);
      } else {
        setIsLogin(false);
      }
    } else {
      setIsLogin(false);
    }
    setLoading(false);
  }, [initialSession]);

  const login = async (credentials) => {
    try {
      setLoading(true);

      const requestBody = {
        ClientChannel: isMobile ? "Mobile" : "Web",
        ...credentials,
      };

      const response = await authService.login(requestBody);

      // Session nesnesini middleware.js'deki gibi oluştur
      if (response) {
        let sessionData;
        sessionData = response;

        try {
          // Session'ı JSON'a çevir
          const sessionJson = JSON.stringify(sessionData);

          // Session'ı cookie'ye kaydet
          Cookies.set(SAKA_COOKIE_NAMES.SESSION, sessionJson, {
            expires: 90,
            path: "/",
            sameSite: "lax",
          });

          // SessionId ve SessionToken'ı da ayrı cookie'lere kaydet
          if (sessionData.SessionId) {
            Cookies.set(SAKA_COOKIE_NAMES.SESSION_ID, sessionData.SessionId, {
              expires: 1,
              path: "/",
              sameSite: "lax",
            });
          }

          if (sessionData.SessionToken) {
            Cookies.set(
              SAKA_COOKIE_NAMES.SESSION_TOKEN,
              sessionData.SessionToken,
              {
                expires: 1,
                path: "/",
                sameSite: "lax",
              },
            );
          }
        } catch (error) {
          console.error("Error setting session cookie:", error);
          // Hata durumunda devam et, middleware session'ı tekrar set edecek
        }
      }

      sendGTMEvent({
        event: "custom_event",
        event_category: "membership",
        event_action: DATA_LAYER_EVENT_NAMES.LOGIN,
        method:
          credentials.AuthenticationType == 0
            ? "Email"
            : credentials.AuthenticationType == 1
              ? "Facebook"
              : credentials.AuthenticationType == 2
                ? "Google"
                : "Apple",
        user_hashed_mail: response?.UserHashedEmail,
        user_id: response?.UserId?.toString() ?? "",
      });

      setTimeout(() => {
        window.location.reload();
      }, 300);

      return { success: true };
    } catch (error) {
      sendGTMEvent({
        event: "custom_event",
        event_category: "failed_events",
        event_action: DATA_LAYER_EVENT_NAMES.LOGIN_FAILED,
        failed_reason: t(
          `${error?.response?.data?.Type}.${error?.response?.data?.Name}`,
        ),
      });

      return {
        success: false,
        error: t(
          `${error?.response?.data?.Type}.${error?.response?.data?.Name}`,
        ),
      };
    } finally {
      setLoading(false);
    }
  };

  const register = async (credentials) => {
    try {
      setLoading(true);

      const requestBody = {
        ClientChannel: isMobile ? "Mobile" : "Web",
        ...credentials,
      };

      const response = await authService.register(requestBody);

      if (response) {
        let sessionData;
        sessionData = response;

        try {
          // Session'ı JSON'a çevir
          const sessionJson = JSON.stringify(sessionData);

          // Session'ı cookie'ye kaydet
          Cookies.set(SAKA_COOKIE_NAMES.SESSION, sessionJson, {
            expires: 90,
            path: "/",
            sameSite: "lax",
          });

          // SessionId ve SessionToken'ı da ayrı cookie'lere kaydet
          if (sessionData.SessionId) {
            Cookies.set(SAKA_COOKIE_NAMES.SESSION_ID, sessionData.SessionId, {
              expires: 1,
              path: "/",
              sameSite: "lax",
            });
          }

          if (sessionData.SessionToken) {
            Cookies.set(
              SAKA_COOKIE_NAMES.SESSION_TOKEN,
              sessionData.SessionToken,
              {
                expires: 1,
                path: "/",
                sameSite: "lax",
              },
            );
          }
        } catch (error) {
          console.error("Error setting session cookie:", error);
          // Hata durumunda devam et, middleware session'ı tekrar set edecek
        }
      }

      debugger;

      sendGTMEvent({
        event: "custom_event",
        event_category: "membership",
        event_action: DATA_LAYER_EVENT_NAMES.SIGN_UP,
        method:
          credentials.AuthenticationType == 0
            ? "Email"
            : credentials.AuthenticationType == 1
              ? "Facebook"
              : credentials.AuthenticationType == 2
                ? "Google"
                : "Apple",
        user_hashed_mail: response?.UserHashedEmail,
        user_id: response?.UserId?.toString() ?? "",
        user_promotions_consent: false,
      });

      setTimeout(() => {
        window.location.reload();
      }, 300);

      return { success: true };
    } catch (error) {
      sendGTMEvent({
        event: "custom_event",
        event_category: "failed_events",
        event_action: DATA_LAYER_EVENT_NAMES.SIGN_UP_FAILED,
        failed_reason: t(
          `${error?.response?.data?.Type}.${error?.response?.data?.Name}`,
        ),
      });

      return {
        success: false,
        error: t(
          `${error?.response?.data?.Type}.${error?.response?.data?.Name}`,
        ),
      };
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      setLoading(true);

      // const response = await authService.logout(requestBody);

      // Login'de setlenen 3 cookie'yi sil
      Cookies.remove(SAKA_COOKIE_NAMES.SESSION, { path: "/" });
      Cookies.remove(SAKA_COOKIE_NAMES.SESSION_ID, { path: "/" });
      Cookies.remove(SAKA_COOKIE_NAMES.SESSION_TOKEN, { path: "/" });

      setTimeout(() => {
        window.location.reload();
      }, 300);

      return { success: true };
    } catch (error) {
      console.error("Logout error:", error);
      return {
        success: false,
        error: t(`${error.response.data.Type}.${error.response.data.Name}`),
      };
    } finally {
      setLoading(false);
    }
  };

  const showLoginModal = useCallback(
    (showRegister = false) => {
      const currentUrl = new URL(window.location.href);
      if (!currentUrl.search) {
        currentUrl.searchParams.set("login", "1");
        window.history.replaceState({}, "", currentUrl.toString());
      }

      if (showRegister) {
        setActiveTab("register");
      }

      loginModal.open({
        showCloseButton: false,
        isBottomSheet: true,
        size: "sm",
      });
    },
    [loginModal],
  );

  const hideLoginModal = useCallback(() => {
    // const currentUrl = new URL(window.location.href);
    // if (currentUrl.searchParams.has("login")) {
    //   currentUrl.searchParams.delete("login");
    //   window.history.replaceState({}, "", currentUrl.toString());
    // }

    loginModal.close();
    setTimeout(() => {
      setActiveTab("login");
    }, 500);
  }, [loginModal]);

  // Kullanıcı adının ilk harflerini al
  const getInitials = useCallback(() => {
    if (!session?.UserDetail) return "T";

    const firstName =
      session.UserDetail.FirstName || session.UserDetail.Name || "";
    const lastName = session.UserDetail.LastName || "";

    const firstInitial = firstName.charAt(0).toUpperCase();
    const lastInitial = lastName.charAt(0).toUpperCase();

    return firstInitial + lastInitial || "U";
  }, [session]);

  // Kullanıcı adını al (fallback text parametre olarak alınır)
  const getUserName = useCallback(
    (fallbackText = "User") => {
      if (!session?.UserDetail) return fallbackText;

      const firstName =
        session.UserDetail.FirstName || session.UserDetail.Name || "";
      const lastName = session.UserDetail.LastName || "";

      return `${firstName} ${lastName}`.trim() || fallbackText;
    },
    [session],
  );

  // Value objesini useMemo ile cache'le - gereksiz re-render'ları önle
  const value = useMemo(
    () => ({
      loading,
      isLogin,
      session,
      login,
      register,
      logout,
      showLoginModal,
      hideLoginModal,
      getInitials,
      getUserName,
      activeTab,
      setActiveTab,
    }),
    [
      loading,
      isLogin,
      session,
      login,
      register,
      logout,
      showLoginModal,
      hideLoginModal,
      getInitials,
      getUserName,
      activeTab,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
